import {PartialGameApiModel} from "./partial-game-api-model";
import {SportDomainModel} from "../../sports/models/sport-domain-model";
import {GameCompetitorDomainModel} from "./game-competitor/game-competitor-domain-model";
import {CompetitionDomainModel} from "../../common/competition/models/competition-domain-model";
import {contractsUtils} from "../../../infrastructure/utils/contracts-utils";
import {BaseDomainModel} from "../../../infrastructure/models/base-domain-model";
import {GAME_STATUSES, GAME_TIME_AND_STATUS_DISPLAY_TYPE, OUTCOME, SCORE_IN_90_MAPPER, LINEUPS_STATUS} from "../../../core/app/consts";
import {WINNER_STATUSES} from "../../../core/consts";
import {PredictionDomainModel} from "../../full-game/models/prediction/prediction.domain-model";
import {PredictionApiModel} from "../../full-game/models/prediction/prediction.api-model";
import {OddsDomainModel} from "../../common/odds/models/odds.domain-model";
import {AnimationDomainModel} from "../../common/animation/animation.domain-model";
import {SummaryDomainModel} from "../../full-game/models/summary/summary.domain-model";
import {StageDomainModel} from "../../full-game/models/stage/stage-domain-model";
import {VenueDomainModel} from "../../full-game/models/venue/venue-domain-model";
import {GameH2hExtraDataDomainModel} from "../../h2h/models/game/game-h2h.extra-data.domain-model";
import {GameH2hExtraDataApiModel} from "../../h2h/models/game/game-h2h.extra-data.api-model";
import {runInAction, action, computed, observable, makeObservable} from 'mobx';
import {OddsPreviewDomainModel} from "../../common/odds-preview/models/odds-preview.domain-model";
import {OpportunityDomainModel} from "../../bets-opportunities/models/opportunities/opportunity.domain-model";
import {PlayerBetPredictionDomainModel} from "../../full-game/models/player-bet-prediction/player-bet-prediction.domain-model";
import type {PlayerBetPredictionApiModel} from "../../full-game/models/player-bet-prediction/player-bet-prediction.api-model";
import {VideoDomainModel} from "../../full-game/models/video/video-domain-model";

export class PartialGameDomainModel extends BaseDomainModel {
  apiModel: PartialGameApiModel;
  updateTime: Number;
  competition: CompetitionDomainModel;
  startTime: Date;
  @observable homeCompetitor: GameCompetitorDomainModel;
  @observable awayCompetitor: GameCompetitorDomainModel;
  sport: SportDomainModel;
  @observable odds: OddsDomainModel;
  summary: SummaryDomainModel;
  @observable stages: Array<StageDomainModel>;
  @observable _statusGroup: Array<StageDomainModel>;
  competitionDisplayName: String;
  competitionGroupByName: String;
  @observable hasLineups: Boolean;
  @observable hasMissingPlayers: Boolean;
  @observable hasFieldPositions: Boolean;
  @observable hasTVNetworks: Boolean;
  @observable hasLiveStreaming: Boolean;
  @observable hasBetsTeaser: Boolean;
  @observable winDescription: String;
  @observable gameTimeDisplay: String;
  @observable shortAggregateText: String;
  @observable aggregateText: String;
  @observable displayTitle: String;
  @observable justEnded: Boolean;
  @observable gameTimeAndStatusDisplayType: Number;
  @observable showCountdown: Boolean;
  statusId: Number;
  seasonNum: Number;
  stageNum: Number;
  groupNum: Number;
  roundNum: Number;
  legNum: Number;
  outcome: OUTCOME;
  @observable statusText: String;
  @observable shortStatusText: String;
  roundName: String;
  stageName: String;
  groupName: String;
  @observable oddsPreview: OddsPreviewDomainModel;
  trendsCount: Number;
  @observable hasNews: Boolean;
  @observable lineupsStatus: LINEUPS_STATUS;
  @observable lineupsStatusText: String;

  constructor(apiModel: PartialGameApiModel, competition: CompetitionDomainModel, homeCompetitor: GameCompetitorDomainModel, awayCompetitor: GameCompetitorDomainModel, startTime: Date, sport: SportDomainModel, odds: OddsDomainModel, summary: SummaryDomainModel, oddsPreview: OddsPreviewDomainModel) {
    super(apiModel);

    contractsUtils.required(competition);
    contractsUtils.required(startTime);
    contractsUtils.required(homeCompetitor);
    contractsUtils.required(awayCompetitor);
    contractsUtils.required(sport);

    this.updateTime = Date.now();
    this.competition = competition;
    this.startTime = startTime;
    this.homeCompetitor = homeCompetitor;
    this.awayCompetitor = awayCompetitor;
    this.sport = sport;
    this.odds = odds;
    this.animation = this.apiModel.animation && new AnimationDomainModel(this.apiModel.animation);
    this.summary = summary;
    this.stages = apiModel.stages?.map(stage => new StageDomainModel(stage));
    this._statusGroup = apiModel.statusGroup;
    this.competitionDisplayName = apiModel.competitionDisplayName;
    this.competitionGroupByName = apiModel.competitionGroupByName;
    this.hasLineups = apiModel.hasLineups;
    this.hasMissingPlayers = apiModel.hasMissingPlayers;
    this.hasFieldPositions = apiModel.hasFieldPositions;
    this.hasTVNetworks = apiModel.hasTVNetworks;
    this.hasLiveStreaming = apiModel.hasLiveStreaming;
    this.hasBetsTeaser = apiModel.hasBetsTeaser;
    this.winDescription = apiModel.winDescription;
    this.gameTimeDisplay = apiModel.gameTimeDisplay;
    this.shortAggregateText = apiModel.shortAggregateText;
    this.aggregateText = apiModel.aggregateText;
    this.displayTitle = apiModel.displayTitle;
    this.justEnded = apiModel.justEnded;
    this.gameTimeAndStatusDisplayType = apiModel.gameTimeAndStatusDisplayType;
    this.showCountdown = apiModel.showCountdown;
    this.statusId = apiModel.statusId;
    this.seasonNum = apiModel.seasonNum;
    this.stageNum = apiModel.stageNum;
    this.groupNum = apiModel.groupNum;
    this.roundNum = apiModel.roundNum;
    this.legNum = apiModel.legNum;
    this.outcome = apiModel.outcome;
    this.statusText = apiModel.statusText;
    this.shortStatusText = apiModel.shortStatusText;
    this.roundName = apiModel.roundName;
    this.stageName = apiModel.stageName;
    this.groupName = apiModel.groupName;
    this.oddsPreview = oddsPreview;
    this.athleteNotInSquad = apiModel.athleteNotInSquad;
    this.bettingOpportunity = apiModel.bettingOpportunity ? new OpportunityDomainModel(apiModel.bettingOpportunity) : null;
    this.trendsCount = apiModel.trendsCount;
    this.hasNews = apiModel.hasNews;
    this.hasStats = apiModel.hasStats;
    this.lineupsStatus = apiModel.lineupsStatus;
    this.lineupsStatusText = apiModel.lineupsStatusText;

    makeObservable(this);
  }

  @computed get matchupId(): String {
    const homeCompetitorId: Number = this.homeCompetitor?.id;
    const awayCompetitorId: Number = this.awayCompetitor?.id;
    const competitionId: Number = this.competition?.id;

    const firstCompetitorId = homeCompetitorId < awayCompetitorId ? homeCompetitorId : awayCompetitorId;
    const secondCompetitorId = homeCompetitorId < awayCompetitorId ? awayCompetitorId : homeCompetitorId;

    const matchupId: String = `${firstCompetitorId}-${secondCompetitorId}-${competitionId}`;

    return matchupId;
  }

  get nameForUrl(): String {
    const isHomeCompetitorFirst: Boolean = this.homeCompetitor.nameForURL < this.awayCompetitor.nameForURL;
    const nameForUrl: String = isHomeCompetitorFirst ? `${this.homeCompetitor.nameForURL}-${this.awayCompetitor.nameForURL}` : `${this.awayCompetitor.nameForURL}-${this.homeCompetitor.nameForURL}`;

    return nameForUrl;
  }

  get isFollow(): Boolean {
    return this.competition?.isFollow && this.homeCompetitor?.isFollow && this.awayCompetitor?.isFollow;
  }

  set statusGroup(statusGroup: GAME_STATUSES) {
    this._statusGroup = statusGroup;
  }

  @computed get statusGroup(): GAME_STATUSES {
    return this._statusGroup;
  }

  @computed get aggregateScore(): String {
    const hasAggregateScore = this.apiModel.homeCompetitor.aggregatedScore > -1 && this.apiModel.awayCompetitor.aggregatedScore > -1;

    return hasAggregateScore ? `${this.apiModel.homeCompetitor.aggregatedScore} - ${this.apiModel.awayCompetitor.aggregatedScore}` : null;
  }

  @computed get isValid() {
    return this.statusGroup !== GAME_STATUSES.anticipated;
  }

  @computed get isLive(): Boolean {
    return this.statusGroup === GAME_STATUSES.started;
  }

  @computed get isEnded(): Boolean {
    return this.statusGroup === GAME_STATUSES.ended;
  }

  @computed get isScheduled(): Boolean {
    return this.statusGroup === GAME_STATUSES.scheduled;
  }

  @computed get predictions(): Array<PredictionDomainModel> {
    const predictions: Array<PredictionDomainModel> = this.apiModel.promotedPredictions?.predictions?.map(
      (prediction: PredictionApiModel) : PredictionDomainModel => new PredictionDomainModel(prediction));

    return predictions;
  }

  @computed get playersBetsPredictions(): Array<PlayerBetPredictionDomainModel> {
    const playersBetsPredictions: Array<PlayerBetPredictionDomainModel> = this.apiModel.promotedPredictions?.playersBetsPredictions?.map(
      (prediction: PlayerBetPredictionApiModel) : PlayerBetPredictionDomainModel => new PlayerBetPredictionDomainModel(prediction));

    return playersBetsPredictions;
  }

  @computed get gameTimeAndStatusText() {
    switch (this.gameTimeAndStatusDisplayType) {
      case(GAME_TIME_AND_STATUS_DISPLAY_TYPE.gameTime):
        return this.gameTimeDisplay;
      case(GAME_TIME_AND_STATUS_DISPLAY_TYPE.statusText):
        return this.statusText;
      case(GAME_TIME_AND_STATUS_DISPLAY_TYPE.gameTimeAndStatusText):
        return this.statusText + (this.gameTimeDisplay ? ' ' + this.gameTimeDisplay : '');
      case(GAME_TIME_AND_STATUS_DISPLAY_TYPE.none):
        return '';
      default:
        return '';
    }
  }

  @computed get gameTimeAndShortStatusText() {
    switch (this.gameTimeAndStatusDisplayType) {
      case(GAME_TIME_AND_STATUS_DISPLAY_TYPE.gameTime):
        return this.gameTimeDisplay;
      case(GAME_TIME_AND_STATUS_DISPLAY_TYPE.statusText):
        return this._shortStatusText;
      case(GAME_TIME_AND_STATUS_DISPLAY_TYPE.gameTimeAndStatusText):
        return this._shortStatusText + (this.gameTimeDisplay ? ' ' + this.gameTimeDisplay : '');
      case(GAME_TIME_AND_STATUS_DISPLAY_TYPE.none):
        return '';
      default:
        return '';
    }
  }

  get venue(): VenueDomainModel {
    const venue: Array<VenueDomainModel> = this.apiModel.venue && new VenueDomainModel(this.apiModel.venue);

    return venue
  }

  @computed get _shortStatusText(): String {
    return (this.shortStatusText || this.statusText);
  }

  calcGameCompetitorStatus(competitorId): Number {
    const isHomeTeam = competitorId === this.homeCompetitor.id;
    if (this.homeCompetitor.isWinner) {
      return isHomeTeam ? WINNER_STATUSES.win : WINNER_STATUSES.lose;
    } else if (this.awayCompetitor.isWinner) {
      return isHomeTeam ? WINNER_STATUSES.lose : WINNER_STATUSES.win;
    } else {
      return WINNER_STATUSES.draw;
    }
  }

  clearAnimation() {
    this.animation = null;
    this.apiModel.animation = null;
  }

  calcGameBadgeIdStatus(competitorId): OUTCOME {
    let outcome = this.outcome;

    return outcome;
  }

  @computed get isContextualUse(): Boolean {
    return this.summary?.isContextualUse;
  }

  @action update(model: PartialGameDomainModel) {
    super.update(model);

    runInAction(() => {
      this.startTime = model.startTime;
      this.animation = model.animation;
      this._statusGroup = model._statusGroup;
      this.competitionDisplayName = model.competitionDisplayName;
      this.hasLineups = model.hasLineups;
      this.hasMissingPlayers = model.hasMissingPlayers;
      this.hasFieldPositions = model.hasFieldPositions;
      this.hasTVNetworks = model.hasTVNetworks;
      this.hasLiveStreaming = model.hasLiveStreaming;
      this.hasBetsTeaser = model.hasBetsTeaser;
      this.winDescription = model.winDescription;
      this.roundNum = model.roundNum;
      this.gameTimeDisplay = model.gameTimeDisplay;
      this.shortAggregateText = model.shortAggregateText;
      this.aggregateText = model.aggregateText;
      this.displayTitle = model.displayTitle;
      this.justEnded = model.justEnded;
      this.gameTimeAndStatusDisplayType = model.gameTimeAndStatusDisplayType;
      this.showCountdown = model.showCountdown;
      this.seasonNum = model.seasonNum;
      this.stageNum = model.stageNum;
      this.outcome = model.outcome;
      this.statusText = model.statusText;
      this.shortStatusText = model.shortStatusText;
      this.trendsCount = model.trendsCount;
      this.addedTime = model.addedTime;
      this.lineupsStatus = model.lineupsStatus;
      this.lineupsStatusText = model.lineupsStatusText;

      if (this.odds) {
        this.odds?.update(model.odds)
      } else {
        this.odds = model.odds && new OddsDomainModel(model.odds);
      }

      if (this.oddsPreview) {
        this.oddsPreview?.update(model.oddsPreview)
      } else {
        this.oddsPreview = model.oddsPreview && new OddsPreviewDomainModel(model.oddsPreview);
      }

      if (model.stages?.length > 0) {
        this.stages = model.stages?.map(stage => new StageDomainModel(stage));
      }

      model.homeCompetitor && this.homeCompetitor?.update(model.homeCompetitor);
      model.awayCompetitor && this.awayCompetitor?.update(model.awayCompetitor);
    })
  }

  get isHomeAwayInverted(): Boolean {
    const isHomeAwayInverted: Boolean = this.apiModel.isHomeAwayInverted ? this.apiModel.isHomeAwayInverted : this.apiModel.homeAwayTeamOrder === 2;
    return isHomeAwayInverted;
  }

  get hasStandings(): Boolean {
    return this.apiModel.hasStandings;
  }

  get standingsName(): String {
    return this.apiModel.standingsName;
  }

  get hasBrackets(): Boolean {
    return this.apiModel.hasBrackets;
  }

  get hasPreviousMeetings(): Boolean {
    return this.apiModel.hasPreviousMeetings;
  }

  get hasRecentMatches(): Boolean {
    return this.apiModel.hasRecentMatches;
  }

  get extraDataTitle(): String {
    return this.apiModel.extraDataTitle;
  }

  get extraData(): Array<GameH2hExtraDataDomainModel> {
    return this.apiModel.extraData?.map((extraData: GameH2hExtraDataApiModel) => new GameH2hExtraDataDomainModel(extraData));
  }

  get gameStageHasTable(): Boolean {
    return this.apiModel.gameStageHasTable;
  }

  get hasTrends(): Boolean {
    return this.apiModel.hasTrends;
  }

  get hasBets(): Boolean {
    return this.apiModel.hasBets;
  }

  get hasPlayerBets(): Boolean {
    return this.apiModel.hasPlayerBets;
  }

  get hasScore(): Boolean {
    return this.homeCompetitor.hasScore && this.awayCompetitor.hasScore;
  }

  get outcomeStatusEndOfGame(): Boolean {
    return SCORE_IN_90_MAPPER[this.outcome];
  }

  get tvNetworkName(): String {
    return this.apiModel.tvNetworkName;
  }

  get hasVideo(): Boolean {
    return this.apiModel.hasVideo;
  }

  get video(): VideoDomainModel {
    const video: VideoDomainModel = this.apiModel.hasVideo ? new VideoDomainModel(this.apiModel.video) : null;

    return video;
  }
}
